import { AnyGame, FootballEvent, FootballGame, GameStatistic, GameStatus, GameTimelineEvent, NBAGame, NFLGame, QuarterScore } from '@/types';

const ESPN_SITE_API = 'https://site.api.espn.com/apis/site/v2/sports';
const CACHE_TTL = 20_000;

interface ESPNLeagueSource {
  sportPath: string;
  slug: string;
  leagueId: string;
  leagueName: string;
  country: string;
  flag: string;
}

const ESPN_SOCCER_SOURCES: ESPNLeagueSource[] = [
  { sportPath: 'soccer', slug: 'fifa.world', leagueId: 'espn-fifa.world', leagueName: 'Copa do Mundo FIFA', country: 'World', flag: '🌍' },
  { sportPath: 'soccer', slug: 'fifa.cwc', leagueId: 'espn-fifa.cwc', leagueName: 'Mundial de Clubes', country: 'World', flag: '🌍' },
  { sportPath: 'soccer', slug: 'uefa.champions', leagueId: 'espn-uefa.champions', leagueName: 'UEFA Champions League', country: 'Europe', flag: '🇪🇺' },
  { sportPath: 'soccer', slug: 'uefa.europa', leagueId: 'espn-uefa.europa', leagueName: 'UEFA Europa League', country: 'Europe', flag: '🇪🇺' },
  { sportPath: 'soccer', slug: 'uefa.europa.conf', leagueId: 'espn-uefa.europa.conf', leagueName: 'UEFA Conference League', country: 'Europe', flag: '🇪🇺' },
  { sportPath: 'soccer', slug: 'conmebol.libertadores', leagueId: 'espn-conmebol.libertadores', leagueName: 'Copa Libertadores', country: 'South America', flag: '🌎' },
  { sportPath: 'soccer', slug: 'conmebol.sudamericana', leagueId: 'espn-conmebol.sudamericana', leagueName: 'Copa Sul-Americana', country: 'South America', flag: '🌎' },
  { sportPath: 'soccer', slug: 'bra.1', leagueId: 'espn-bra.1', leagueName: 'Brasileirao Serie A', country: 'Brazil', flag: '🇧🇷' },
  { sportPath: 'soccer', slug: 'bra.2', leagueId: 'espn-bra.2', leagueName: 'Brasileirao Serie B', country: 'Brazil', flag: '🇧🇷' },
  { sportPath: 'soccer', slug: 'bra.cup', leagueId: 'espn-bra.cup', leagueName: 'Copa do Brasil', country: 'Brazil', flag: '🇧🇷' },
  { sportPath: 'soccer', slug: 'eng.1', leagueId: 'espn-eng.1', leagueName: 'Premier League', country: 'England', flag: '🏴' },
  { sportPath: 'soccer', slug: 'esp.1', leagueId: 'espn-esp.1', leagueName: 'La Liga', country: 'Spain', flag: '🇪🇸' },
  { sportPath: 'soccer', slug: 'ita.1', leagueId: 'espn-ita.1', leagueName: 'Serie A', country: 'Italy', flag: '🇮🇹' },
  { sportPath: 'soccer', slug: 'ger.1', leagueId: 'espn-ger.1', leagueName: 'Bundesliga', country: 'Germany', flag: '🇩🇪' },
  { sportPath: 'soccer', slug: 'fra.1', leagueId: 'espn-fra.1', leagueName: 'Ligue 1', country: 'France', flag: '🇫🇷' },
  { sportPath: 'soccer', slug: 'por.1', leagueId: 'espn-por.1', leagueName: 'Liga Portugal', country: 'Portugal', flag: '🇵🇹' },
  { sportPath: 'soccer', slug: 'ned.1', leagueId: 'espn-ned.1', leagueName: 'Eredivisie', country: 'Netherlands', flag: '🇳🇱' },
  { sportPath: 'soccer', slug: 'arg.1', leagueId: 'espn-arg.1', leagueName: 'Liga Argentina', country: 'Argentina', flag: '🇦🇷' },
  { sportPath: 'soccer', slug: 'usa.1', leagueId: 'espn-usa.1', leagueName: 'MLS', country: 'USA', flag: '🇺🇸' },
  { sportPath: 'soccer', slug: 'mex.1', leagueId: 'espn-mex.1', leagueName: 'Liga MX', country: 'Mexico', flag: '🇲🇽' },
];

const cache = new Map<string, { data: unknown; ts: number }>();

function todayKey(): string {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
}

function compactDateKey(date?: string): string {
  return date ? date.replace(/-/g, '') : todayKey();
}

function cached<T>(key: string, ttl: number, fetcher: () => Promise<T>): Promise<T> {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.ts < ttl) return Promise.resolve(entry.data as T);
  return fetcher().then((data) => {
    cache.set(key, { data, ts: Date.now() });
    return data;
  });
}

function mapStatus(event: any): GameStatus {
  const status = event.status ?? event.competitions?.[0]?.status;
  const state = status?.type?.state ?? '';
  const name = status?.type?.name ?? '';
  if (state === 'post' || status?.type?.completed) return 'finished';
  if (state === 'in') return name === 'STATUS_HALFTIME' ? 'halftime' : 'live';
  return 'scheduled';
}

function eventMinute(detail: any): number {
  const value = detail?.clock?.displayValue ?? detail?.clock?.value ?? '';
  const match = String(value).match(/\d+/);
  return match ? Number(match[0]) : 0;
}

function mapFootballEvent(detail: any, homeId: string): FootballEvent | null {
  const typeText = String(detail?.type?.text ?? '').toLowerCase();
  const teamId = String(detail?.team?.id ?? '');
  const player = detail?.athletesInvolved?.[0]?.displayName;

  if (!typeText) return null;

  let type: FootballEvent['type'] | null = null;
  if (detail?.scoringPlay || typeText.includes('goal')) type = detail?.ownGoal ? 'own_goal' : detail?.penaltyKick ? 'penalty' : 'goal';
  else if (detail?.redCard || typeText.includes('red card')) type = 'red_card';
  else if (detail?.yellowCard || typeText.includes('yellow card')) type = 'yellow_card';
  else if (typeText.includes('substitution')) type = 'substitution';

  if (!type) return null;

  return {
    minute: eventMinute(detail),
    type,
    team: teamId === homeId ? 'home' : 'away',
    player,
  };
}

function footballEventToTimeline(event: FootballEvent, index: number): GameTimelineEvent {
  const title =
    event.type === 'goal' ? 'Gol'
    : event.type === 'own_goal' ? 'Gol contra'
    : event.type === 'penalty' ? 'Gol de pênalti'
    : event.type === 'yellow_card' ? 'Cartão amarelo'
    : event.type === 'red_card' ? 'Cartão vermelho'
    : 'Substituição';

  return {
    id: `${event.minute}-${event.type}-${event.player ?? 'event'}-${index}`,
    side: event.team,
    minute: `${event.minute}'`,
    type: event.type === 'substitution'
      ? 'substitution'
      : event.type === 'yellow_card' || event.type === 'red_card'
        ? 'card'
        : 'goal',
    title,
    detail: event.player,
  };
}

function footballStatusTimeline(game: FootballGame): GameTimelineEvent[] {
  const items: GameTimelineEvent[] = [];
  if (game.status !== 'scheduled') {
    items.push({ id: `${game.id}:start`, side: 'center', type: 'game_start', title: 'Início da partida' });
  }
  if (game.status === 'halftime' || game.status === 'finished' || (game.minute ?? 0) > 45) {
    items.push({ id: `${game.id}:ht`, side: 'center', type: 'halftime', title: 'Fim do primeiro tempo' });
  }
  if (game.status === 'live' && (game.minute ?? 0) > 45) {
    items.push({ id: `${game.id}:2h`, side: 'center', type: 'period_start', title: 'Começou o segundo tempo' });
  }
  if (game.status === 'finished') {
    items.push({ id: `${game.id}:ft`, side: 'center', type: 'game_end', title: 'Fim de jogo' });
  }
  return items;
}

function statNumber(value: unknown): number | undefined {
  const parsed = Number.parseFloat(String(value ?? '').replace('%', '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function mapESPNTeamStats(summary: any): GameStatistic[] | undefined {
  const teams: any[] = summary?.boxscore?.teams ?? [];
  const home = teams.find((team) => team.homeAway === 'home');
  const away = teams.find((team) => team.homeAway === 'away');
  if (!home || !away) return undefined;
  const homeStats = new Map((home.statistics ?? []).map((stat: any) => [stat.name ?? stat.label, stat.displayValue ?? stat.value]));
  const awayStats = new Map((away.statistics ?? []).map((stat: any) => [stat.name ?? stat.label, stat.displayValue ?? stat.value]));
  const keys = Array.from(new Set([...homeStats.keys(), ...awayStats.keys()])).slice(0, 12);
  const rows: GameStatistic[] = [];
  keys.forEach((key) => {
    const homeValue = homeStats.get(key);
    const awayValue = awayStats.get(key);
    if (homeValue == null && awayValue == null) return;
    rows.push({
      id: String(key),
      label: String(key),
      home: String(homeValue ?? '-'),
      away: String(awayValue ?? '-'),
      homeValue: statNumber(homeValue),
      awayValue: statNumber(awayValue),
    });
  });
  return rows.length > 0 ? rows : undefined;
}

function mapFootballGame(event: any, source: ESPNLeagueSource): FootballGame | null {
  const comp = event.competitions?.[0] ?? event;
  const competitors = comp.competitors ?? [];
  const home = competitors.find((c: any) => c.homeAway === 'home');
  const away = competitors.find((c: any) => c.homeAway === 'away');
  if (!home?.team || !away?.team) return null;

  const status = event.status ?? comp.status;
  const detail = status?.type?.shortDetail ?? status?.type?.detail ?? '';
  const minuteMatch = String(detail).match(/^(\d+)'/);
  const homeId = String(home.team.id ?? '');

  const game: FootballGame = {
    id: `espn-${event.id}`,
    sport: 'futebol',
    league: {
      id: source.leagueId,
      name: source.leagueName,
      logo: `https://a.espncdn.com/i/leaguelogos/soccer/500/${source.slug}.png`,
      country: source.country,
      flag: source.flag,
    },
    homeTeam: {
      id: `espn-${homeId}`,
      name: home.team.displayName ?? home.team.name ?? '',
      shortName: home.team.abbreviation ?? home.team.shortDisplayName ?? '',
      logo: home.team.logo ?? home.team.logos?.[0]?.href ?? '',
    },
    awayTeam: {
      id: `espn-${away.team.id ?? ''}`,
      name: away.team.displayName ?? away.team.name ?? '',
      shortName: away.team.abbreviation ?? away.team.shortDisplayName ?? '',
      logo: away.team.logo ?? away.team.logos?.[0]?.href ?? '',
    },
    score: {
      home: Number(home.score ?? 0),
      away: Number(away.score ?? 0),
    },
    status: mapStatus(event),
    minute: minuteMatch ? Number(minuteMatch[1]) : undefined,
    startTime: event.date ?? comp.startDate ?? '',
    venue: comp.venue?.fullName,
    events: (comp.details ?? event.details ?? [])
      .map((detail: any) => mapFootballEvent(detail, homeId))
      .filter((detail: FootballEvent | null): detail is FootballEvent => detail !== null),
  };

  game.timeline = [
    ...footballStatusTimeline(game),
    ...(game.events ?? []).map(footballEventToTimeline),
  ];
  return game;
}

function mapNBAGame(event: any): NBAGame | null {
  const comp = event.competitions?.[0] ?? event;
  const competitors = comp.competitors ?? [];
  const home = competitors.find((c: any) => c.homeAway === 'home');
  const away = competitors.find((c: any) => c.homeAway === 'away');
  if (!home?.team || !away?.team) return null;

  const homeLines: any[] = home.linescores ?? [];
  const awayLines: any[] = away.linescores ?? [];
  const quarterScores: QuarterScore[] = homeLines.map((line, index) => ({
    period: index + 1,
    home: Number(line.value ?? 0),
    away: Number(awayLines[index]?.value ?? 0),
  }));
  const status = event.status ?? comp.status;

  return {
    id: `espn-nba-${event.id}`,
    sport: 'nba',
    homeTeam: {
      id: `espn-nba-${home.team.id ?? ''}`,
      name: home.team.displayName ?? home.team.name ?? '',
      shortName: home.team.abbreviation ?? '',
      logo: home.team.logo ?? home.team.logos?.[0]?.href ?? '',
    },
    awayTeam: {
      id: `espn-nba-${away.team.id ?? ''}`,
      name: away.team.displayName ?? away.team.name ?? '',
      shortName: away.team.abbreviation ?? '',
      logo: away.team.logo ?? away.team.logos?.[0]?.href ?? '',
    },
    score: { home: Number(home.score ?? 0), away: Number(away.score ?? 0) },
    status: mapStatus(event),
    period: status?.period ?? 0,
    clock: status?.displayClock !== '0:00' ? status?.displayClock : undefined,
    startTime: event.date ?? comp.startDate ?? '',
    quarter_scores: quarterScores.length > 0 ? quarterScores : undefined,
  };
}

function mapNFLGame(event: any): NFLGame | null {
  const comp = event.competitions?.[0] ?? event;
  const competitors = comp.competitors ?? [];
  const home = competitors.find((c: any) => c.homeAway === 'home');
  const away = competitors.find((c: any) => c.homeAway === 'away');
  if (!home?.team || !away?.team) return null;

  const status = event.status ?? comp.status;
  const detail = status?.type?.shortDetail ?? '';
  const quarterMatch = String(detail).match(/Q(\d)/);

  return {
    id: `espn-nfl-${event.id}`,
    sport: 'nfl',
    homeTeam: {
      id: `espn-nfl-${home.team.id ?? ''}`,
      name: home.team.displayName ?? home.team.name ?? '',
      shortName: home.team.abbreviation ?? '',
      logo: home.team.logo ?? home.team.logos?.[0]?.href ?? '',
    },
    awayTeam: {
      id: `espn-nfl-${away.team.id ?? ''}`,
      name: away.team.displayName ?? away.team.name ?? '',
      shortName: away.team.abbreviation ?? '',
      logo: away.team.logo ?? away.team.logos?.[0]?.href ?? '',
    },
    score: { home: Number(home.score ?? 0), away: Number(away.score ?? 0) },
    status: mapStatus(event),
    quarter: quarterMatch ? Number(quarterMatch[1]) : undefined,
    clock: status?.displayClock !== '0:00' ? status?.displayClock : undefined,
    startTime: event.date ?? comp.startDate ?? '',
    week: comp.week?.number ?? undefined,
  };
}

async function getJson(url: string): Promise<any | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

async function fetchSoccerSource(source: ESPNLeagueSource, date: string): Promise<FootballGame[]> {
  const json = await getJson(`${ESPN_SITE_API}/${source.sportPath}/${source.slug}/scoreboard?dates=${date}&limit=300`);
  const events: any[] = json?.events ?? [];
  return events
    .map((event) => mapFootballGame(event, source))
    .filter((game): game is FootballGame => game !== null);
}

export async function getESPNAllFootballGamesByDate(dateKey?: string): Promise<FootballGame[]> {
  const date = compactDateKey(dateKey);
  return cached(`espn:all:football:${date}`, CACHE_TTL, async () => {
    const settled = await Promise.allSettled(ESPN_SOCCER_SOURCES.map((source) => fetchSoccerSource(source, date)));
    const games = settled.flatMap((result) => result.status === 'fulfilled' ? result.value : []);
    console.log(`[ESPN All] Futebol ${date}: ${games.length} jogos em ${ESPN_SOCCER_SOURCES.length} fontes`);
    return games;
  });
}

export async function getESPNAllFootballTodayGames(): Promise<FootballGame[]> {
  return getESPNAllFootballGamesByDate();
}

export async function getESPNFootballGameDetail(gameId: string, leagueId?: string): Promise<FootballGame | null> {
  const rawEventId = gameId.replace(/^espn-/, '');
  const source = ESPN_SOCCER_SOURCES.find((item) => item.leagueId === leagueId)
    ?? ESPN_SOCCER_SOURCES.find((item) => leagueId?.endsWith(item.slug))
    ?? ESPN_SOCCER_SOURCES[0];

  return cached(`espn:football:detail:${source.slug}:${rawEventId}`, CACHE_TTL, async () => {
    const json = await getJson(`${ESPN_SITE_API}/${source.sportPath}/${source.slug}/summary?event=${rawEventId}`);
    const event = json?.header;
    if (!event?.competitions?.length) return null;
    const game = mapFootballGame(event, source);
    if (!game) return null;
    game.statistics = mapESPNTeamStats(json);
    return game;
  });
}

export async function getESPNAllNBATodayGames(): Promise<NBAGame[]> {
  const date = todayKey();
  return cached(`espn:all:nba:${date}`, CACHE_TTL, async () => {
    const json = await getJson(`${ESPN_SITE_API}/basketball/nba/scoreboard?dates=${date}&limit=200`);
    const events: any[] = json?.events ?? [];
    return events.map(mapNBAGame).filter((game): game is NBAGame => game !== null);
  });
}

export async function getESPNAllNFLTodayGames(): Promise<NFLGame[]> {
  const date = todayKey();
  return cached(`espn:all:nfl:${date}`, CACHE_TTL, async () => {
    const json = await getJson(`${ESPN_SITE_API}/football/nfl/scoreboard?dates=${date}&limit=200`);
    const events: any[] = json?.events ?? [];
    return events.map(mapNFLGame).filter((game): game is NFLGame => game !== null);
  });
}

export async function getESPNAllGamesToday(): Promise<AnyGame[]> {
  return cached(`espn:all:games:${todayKey()}`, CACHE_TTL, async () => {
    const [football, nba, nfl] = await Promise.all([
      getESPNAllFootballTodayGames(),
      getESPNAllNBATodayGames(),
      getESPNAllNFLTodayGames(),
    ]);
    return [...football, ...nba, ...nfl];
  });
}
